<script setup lang="ts">
import './assets/style.scss'
import { useRoute } from 'vue-router'
import { computed, useAttrs } from 'vue'
import { VSelectSearch } from '@/shared/ui'
import { useLoadSubTypesList } from '@/entities/Inventory/ObjectSubTypes/model/api.ts'
import {
  resolveObjectSubTypeDisplayValue,
  NULL_SELECT_VALUE,
  NULL_SELECT_TITLE,
  type ObjectSubTypeOption,
} from '@/entities/Inventory/ObjectSubTypes/model/helpers.ts'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  typeId: {
    type: Number,
    default: 0,
  },
  label: {
    type: String,
    default: '',
  },
  fallbackItems: {
    type: Array,
    default: () => [],
  },
})

const route = useRoute()
const attrs = useAttrs()

const objectId = computed(() => String(route.params.object_id ?? ''))
const objectType = computed(() => props.typeId)

const rawValue = defineModel<unknown>()

const emit = defineEmits<{ 'update:menu': [open: boolean]; pointerdown: [event: PointerEvent] }>()

const { data, isPending, isFetching } = useLoadSubTypesList(objectId, objectType)

const items = computed(() => {
  const loaded = Array.isArray(data.value?.data?.sub_types) ? data.value.data.sub_types : []
  const fallback = props.fallbackItems as ObjectSubTypeOption[]
  const loadedIds = new Set(loaded.map((item: ObjectSubTypeOption) => String(item.id)))
  return [...loaded, ...fallback.filter((item) => !loadedIds.has(String(item.id)))]
})

/** Множественный режим несовместим с опцией «пусто» — заглушку не добавляем. */
const isMultiple = computed(() => Boolean(attrs.multiple))

/** Опция «пусто» первой, чтобы значение всегда можно было сбросить в null. */
const selectItems = computed(() => {
  if (isMultiple.value) return items.value
  return [{ id: NULL_SELECT_VALUE, name: NULL_SELECT_TITLE }, ...items.value]
})

/** Значения, которые VSelectSearch не должен выкидывать при поиске. */
const preserveValues = computed(() => (isMultiple.value ? [] : [NULL_SELECT_VALUE]))

const selectLoading = computed(() => Boolean(props.typeId) && (isPending.value || isFetching.value))

/**
 * API и рантайм могут хранить id как строку, а в справочнике — как число.
 * Нормализуем в computed, чтобы VSelect сразу показывал название, а не сырой id.
 * Пустое значение отображаем опцией «пусто», но в модель сохраняем null.
 */
const normalizedValue = computed({
  get: () => {
    const resolved = resolveObjectSubTypeDisplayValue(
      rawValue.value,
      items.value as ObjectSubTypeOption[],
    )
    if (isMultiple.value) return resolved
    return resolved == null || resolved === '' ? NULL_SELECT_VALUE : resolved
  },
  set: (val: unknown) => {
    if (!isMultiple.value && val === NULL_SELECT_VALUE) {
      rawValue.value = ''
      return
    }
    rawValue.value = val
  },
})
</script>

<template>
  <VSelectSearch
    v-bind="attrs"
    v-model="normalizedValue"
    :items="selectItems"
    :loading="selectLoading"
    :disabled="!typeId"
    :label
    :hide-details="attrs.error ? false : (attrs['hide-details'] ?? attrs.hideDetails ?? true)"
    :preserve-values="preserveValues"
    item-value="id"
    item-title="name"
    @pointerdown="(event: PointerEvent) => emit('pointerdown', event)"
    @update:menu="(open: boolean) => emit('update:menu', open)"
  />
</template>
